import { AppNotification, NotificationSettings, PushNotificationPayload, NotificationPermission } from '../types/notification';
import api from './api';
import { config } from '../config';

// Backend notification settings interface (snake_case)
interface BackendNotificationSettings {
    id: number;
    user_id: number;
    push_notifications_enabled: boolean;
    reminder_hours: number;
    job_card_assigned: boolean;
    job_card_reminders: boolean;
    task_completed: boolean;
    job_card_closed: boolean;
    system_announcements: boolean;
    created_at: string;
    updated_at: string;
}

// Admin settings interface
interface AdminSettings {
    default_reminder_hours?: number;
    push_notifications_enabled?: boolean;
}

class NotificationService {
    private static instance: NotificationService;
    private registration: ServiceWorkerRegistration | null = null;
    private isInitialized = false;

    private constructor() {}

    public static getInstance(): NotificationService {
        if (!NotificationService.instance) {
            NotificationService.instance = new NotificationService();
        }
        return NotificationService.instance;
    }

    /**
     * Initialize the notification service
     */
    public async initialize(): Promise<void> {
        if (this.isInitialized) return;

        try {
            // Check if service workers are supported
            if ('serviceWorker' in navigator) {
                this.registration = await navigator.serviceWorker.ready;
                console.log('Service Worker ready for notifications');
            }

            // Request notification permission
            await this.requestPermission();
            
            this.isInitialized = true;
        } catch (error) {
            console.error('Failed to initialize notification service:', error);
        }
    }

    /**
     * Request notification permission from the user
     */
    public async requestPermission(): Promise<NotificationPermission> {
        if (!('Notification' in window)) {
            return { granted: false, denied: true, default: false };
        }

        if (Notification.permission === 'granted') {
            return { granted: true, denied: false, default: false };
        }

        if (Notification.permission === 'denied') {
            return { granted: false, denied: true, default: false };
        }

        try {
            const permission = await Notification.requestPermission();
            return {
                granted: permission === 'granted',
                denied: permission === 'denied',
                default: permission === 'default'
            };
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return { granted: false, denied: true, default: false };
        }
    }

    /**
     * Check current notification permission status
     */
    public getPermissionStatus(): NotificationPermission {
        if (!('Notification' in window)) {
            return { granted: false, denied: true, default: false };
        }

        return {
            granted: Notification.permission === 'granted',
            denied: Notification.permission === 'denied',
            default: Notification.permission === 'default'
        };
    }

    /**
     * Get the raw browser notification permission
     */
    public getBrowserPermission(): string | null {
        if (!('Notification' in window)) {
            return null;
        }
        return Notification.permission;
    }

    /**
     * Check if notifications are supported by the browser
     */
    public isNotificationSupported(): boolean {
        return 'Notification' in window && 'serviceWorker' in navigator;
    }

    /**
     * Check if user has granted notification permission
     */
    public hasPermission(): boolean {
        return this.getPermissionStatus().granted;
    }

    /**
     * Check if user has denied notification permission
     */
    public isPermissionDenied(): boolean {
        return this.getPermissionStatus().denied;
    }

    /**
     * Check if permission has not been requested yet
     */
    public isPermissionDefault(): boolean {
        return this.getPermissionStatus().default;
    }

    /**
     * Send a push notification
     */
    public async sendPushNotification(payload: PushNotificationPayload): Promise<void> {
        if (!this.registration || !this.getPermissionStatus().granted) {
            console.warn('Push notifications not available or permission not granted');
            return;
        }

        try {
            // Check if icon files exist, fallback to default if not
            const iconUrl = payload.icon || '/icons/icon-192x192.png';
            const badgeUrl = payload.badge || '/icons/icon-72x72.png';
            
            // Create notification options with fallback handling
            const notificationOptions: NotificationOptions = {
                body: payload.body,
                data: payload.data,
                tag: payload.tag,
                requireInteraction: payload.requireInteraction || false
            };

            // Only add icon if it exists, otherwise let browser use default
            try {
                const iconResponse = await fetch(iconUrl, { method: 'HEAD' });
                if (iconResponse.ok) {
                    notificationOptions.icon = iconUrl;
                }
            } catch {
                console.warn('Icon not found, using browser default:', iconUrl);
            }

            // Only add badge if it exists, otherwise let browser use default
            try {
                const badgeResponse = await fetch(badgeUrl, { method: 'HEAD' });
                if (badgeResponse.ok) {
                    notificationOptions.badge = badgeUrl;
                }
            } catch {
                console.warn('Badge not found, using browser default:', badgeUrl);
            }

            await this.registration.showNotification(payload.title, notificationOptions);
        } catch (error) {
            console.error('Failed to send push notification:', error);
        }
    }

    /**
     * Get user's notification settings
     */
    public async getNotificationSettings(): Promise<NotificationSettings> {
        try {
            const response = await api.get('/notifications/settings');
            // Convert snake_case to camelCase for frontend compatibility
            return this.convertToFrontendFormat(response.data);
        } catch (error) {
            console.error('Failed to fetch notification settings:', error);
            throw error;
        }
    }

    /**
     * Convert backend snake_case settings to frontend camelCase format
     */
    private convertToFrontendFormat(backendSettings: BackendNotificationSettings): NotificationSettings {
        return {
            id: backendSettings.id,
            userId: backendSettings.user_id,
            pushNotificationsEnabled: backendSettings.push_notifications_enabled,
            reminderHours: backendSettings.reminder_hours,
            jobCardAssigned: backendSettings.job_card_assigned,
            jobCardReminders: backendSettings.job_card_reminders,
            taskCompleted: backendSettings.task_completed,
            jobCardClosed: backendSettings.job_card_closed,
            systemAnnouncements: backendSettings.system_announcements,
            createdAt: backendSettings.created_at,
            updatedAt: backendSettings.updated_at,
        };
    }

    /**
     * Update user's notification settings
     */
    public async updateNotificationSettings(settings: Partial<NotificationSettings>): Promise<NotificationSettings> {
        try {
            // Convert camelCase to snake_case for backend compatibility
            const backendSettings = this.convertToBackendFormat(settings);
            const response = await api.put('/notifications/settings', backendSettings);
            return response.data;
        } catch (error) {
            console.error('Failed to update notification settings:', error);
            throw error;
        }
    }

    /**
     * Convert frontend camelCase settings to backend snake_case format
     */
    private convertToBackendFormat(settings: Partial<NotificationSettings>): Partial<BackendNotificationSettings> {
        const backendSettings: Partial<BackendNotificationSettings> = {};
        
        if (settings.pushNotificationsEnabled !== undefined) {
            backendSettings.push_notifications_enabled = settings.pushNotificationsEnabled;
        }
        if (settings.reminderHours !== undefined) {
            backendSettings.reminder_hours = settings.reminderHours;
        }
        if (settings.jobCardAssigned !== undefined) {
            backendSettings.job_card_assigned = settings.jobCardAssigned;
        }
        if (settings.jobCardReminders !== undefined) {
            backendSettings.job_card_reminders = settings.jobCardReminders;
        }
        if (settings.taskCompleted !== undefined) {
            backendSettings.task_completed = settings.taskCompleted;
        }
        if (settings.jobCardClosed !== undefined) {
            backendSettings.job_card_closed = settings.jobCardClosed;
        }
        if (settings.systemAnnouncements !== undefined) {
            backendSettings.system_announcements = settings.systemAnnouncements;
        }

        return backendSettings;
    }

    /**
     * Get user's notifications
     */
    public async getNotifications(page: number = 1, limit: number = 20): Promise<{ data: AppNotification[], total: number, unread: number }> {
        try {
            const response = await api.get(`/notifications?page=${page}&limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
            throw error;
        }
    }

    /**
     * Mark notification as read
     */
    public async markAsRead(notificationId: string): Promise<void> {
        try {
            await api.patch(`/notifications/${notificationId}/read`);
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
            throw error;
        }
    }

    /**
     * Mark all notifications as read
     */
    public async markAllAsRead(): Promise<void> {
        try {
            await api.patch('/notifications/mark-all-read');
        } catch (error) {
            console.error('Failed to mark all notifications as read:', error);
            throw error;
        }
    }

    /**
     * Delete a notification
     */
    public async deleteNotification(notificationId: string): Promise<void> {
        try {
            await api.delete(`/notifications/${notificationId}`);
        } catch (error) {
            console.error('Failed to delete notification:', error);
            throw error;
        }
    }

    /**
     * Subscribe to push notifications
     */
    public async subscribeToPushNotifications(): Promise<string | null> {
        if (!this.registration) {
            console.warn('Service worker not available');
            return null;
        }

        // Check if VAPID key is configured
        if (!config.VAPID_PUBLIC_KEY) {
            // Provide context to help diagnose common causes: wrong env file for current mode,
            // missing VITE_ prefix, or stale service worker using an old build without the key.
            const mode = (import.meta as any)?.env?.MODE;
            const hasVarName = typeof (import.meta as any)?.env?.VITE_VAPID_PUBLIC_KEY !== 'undefined';
            const length = ((import.meta as any)?.env?.VITE_VAPID_PUBLIC_KEY || '').length;
            console.warn(
                'VAPID public key not configured. Please set VITE_VAPID_PUBLIC_KEY in your environment file.',
                {
                    mode,
                    viteVarDefined: hasVarName,
                    viteVarLength: length,
                }
            );
            return null;
        }

        try {
            const vapidKey = this.urlBase64ToUint8Array(config.VAPID_PUBLIC_KEY);
            const subscription = await this.registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: vapidKey as unknown as ArrayBuffer
            });

            // Send subscription to server
            const response = await api.post('/notifications/subscribe', {
                subscription: subscription.toJSON()
            });

            return response.data.subscriptionId;
        } catch (error) {
            console.error('Failed to subscribe to push notifications:', error);
            try { (window as any)?.toast?.error?.('Failed to enable push notifications'); } catch {}
            return null;
        }
    }

    /**
     * Unsubscribe from push notifications
     */
    public async unsubscribeFromPushNotifications(): Promise<void> {
        if (!this.registration) {
            return;
        }

        try {
            const subscription = await this.registration.pushManager.getSubscription();
            if (subscription) {
                await subscription.unsubscribe();
                await api.post('/notifications/unsubscribe');
                try { (window as any)?.toast?.success?.('Push notifications disabled'); } catch {}
            }
        } catch (error) {
            console.error('Failed to unsubscribe from push notifications:', error);
            try { (window as any)?.toast?.error?.('Failed to disable push notifications'); } catch {}
        }
    }

    /**
     * Convert VAPID key to Uint8Array
     */
    private urlBase64ToUint8Array(base64String: string): Uint8Array {
        if (!base64String) {
            throw new Error('VAPID key is empty or undefined');
        }

        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    /**
     * Get admin notification settings
     */
    public async getAdminNotificationSettings(): Promise<AdminSettings> {
        try {
            const response = await api.get('/admin/notifications/settings');
            return response.data;
        } catch (error) {
            console.error('Failed to fetch admin notification settings:', error);
            throw error;
        }
    }

    /**
     * Update admin notification settings
     */
    public async updateAdminNotificationSettings(settings: AdminSettings): Promise<AdminSettings> {
        try {
            const response = await api.put('/admin/notifications/settings', settings);
            return response.data;
        } catch (error) {
            console.error('Failed to update admin notification settings:', error);
            throw error;
        }
    }
}

export default NotificationService.getInstance();
